import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    IconButton,
    List,
    ListItem,
    Skeleton,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import {ExpandLess, ExpandMore} from '@mui/icons-material';
import AdjustDialogLauncher from '@/pages/components/audit/adjustment/adjust/support/DialogLauncher';
import {type AuditFsTreeSelection} from '@/services/auditService/auditAdjustOrchestrate';
import {useFsAdjustStore} from '@/store/audit/fsAdjustStore';
import {useAdjustDraftStore} from '@/store/audit/adjustDraftStore';
import {
    classifyAdjTargetKind,
    resolveAllowedSliceKeys,
    resolveDrawerEffectiveSliceKey,
    shouldAttachDrawerYoaPayload,
    shouldRequireDrawerYoa,
    shouldShowDrawerYoaSelector,
    type AdjPeriodLockContext,
} from '@/utils/auditPeriodWpMulti';
import type {AuditPeriodSlice} from '@/utils/auditPeriodSplit';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {
    EXCLUSIVE_CODES,
    isAlwaysHiddenFsSelectionCode,
    isCrossExclusiveAdjustmentTarget,
} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {
    isLoanFieldFsCode,
    isPpeDepnNbvCrossFieldDetail,
    getPpeDepnNbvCrossFieldKey,
    resolvePpeDepnNbvOpposite,
    isIaAmorNbvCrossFieldDetail,
    getIaAmorNbvCrossFieldKey,
    resolveIaAmorNbvOpposite,
    iaOwnedFieldKey,
    isPpeScheduleLevelNbvOrDepnContext,
    isIaScheduleLevelNbvOrAmorContext,
} from '@/adjustment/adjust/utils/consolidateWpAdjustments';
import {isTaxWpHiddenFsSelectionTarget} from '@/wp/tax/taxWpFromPaper';
import {getWorkingPaper} from '@/services/auditService/auditWorkingPaperService';
import {
    buildPpeCategoryOptions,
    type PpeCategoryOption,
} from '@/adjustment/adjust/dialogs/asset/ppeTitleUtils';
import {isPpeCategoryFetchCode} from '@/adjustment/adjust/dialogs/utils/initial-data-factories';
import {
    INV_ADJUSTMENT_CODE,
    normalizeFsCode,
    resolveInventoriesToPaperCode,
} from '@/adjustment/adjust/utils/companion-codes';

/** 仅 BS-NCA-PPE / PL-AE-DEP；本地判定，避免依赖 consolidateWpAdjustments 的 field-adj 语义 */
function isPpeOrDepFsCode(code?: string | null): boolean {
    const upper = String(code || '').trim().toUpperCase();
    return upper === 'BS-NCA-PPE' || upper === 'PL-AE-DEP';
}

/** 仅 BS-NCA-IA / PL-AE-AIA / BS-NCA-ITL；WP_TO_WP 下从 EXCLUSIVE_CODES 豁免，可直接选中叶子 */
function isIaOrAiaFsCode(code?: string | null): boolean {
    const upper = String(code || '').trim().toUpperCase();
    return upper === 'BS-NCA-IA' || upper === 'PL-AE-AIA' || upper === 'BS-NCA-ITL';
}

/** PL-AE-DEP MUI-List: Taxi License → ITL WP (wpFetchCode = BS-NCA-ITL) */
const PL_AE_DEP_TAXI_LICENSE_OPTION = {
    key: 'BS-NCA-ITL',
    label: 'Taxi License',
} as const;

/**
 * IntangibleFieldAdjustPanel 出站：owned field 标量（slim 顶栏或 nest 到行上）。
 * 不把 GET 回挂的 adjustments.*Adjs 列表算作 field-adj 发起，避免整表 WP→WP 误放开互斥对端。
 */
function detailHasOutboundIaOwnedFieldAdj(
    detail: unknown,
    ownedKey: 'amorChgAdj' | 'nbvCfAdj',
): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const d = detail as Record<string, any>;
    if (Math.abs(Number(d[ownedKey]) || 0) >= 0.001) return true;
    const rows = d.intangibleRows;
    if (!Array.isArray(rows)) return false;
    return rows.some((row: any) => (
        row && typeof row === 'object' && Math.abs(Number(row[ownedKey]) || 0) >= 0.001
    ));
}

/**
 * Amor↔NBV：from NBV C/F (BS-NCA-IA) → 允许选 PL-AE-AIA；
 * from Amor chg (PL-AE-AIA) → 允许选 BS-NCA-IA。
 * 覆盖 slim + IntangibleFieldAdjustPanel nest 出站；仅 Drawer 互斥豁免用。
 */
function resolveIaAmorNbvCrossForDrawer(
    fromCode: string | null | undefined,
    detail: unknown,
): {active: boolean; oppositeToCode: string | null} {
    if (!fromCode || !isIaOrAiaFsCode(fromCode)) {
        return {active: false, oppositeToCode: null};
    }
    if (isIaAmorNbvCrossFieldDetail(detail, fromCode)) {
        const fieldKey = getIaAmorNbvCrossFieldKey(detail, fromCode);
        const opposite = resolveIaAmorNbvOpposite(fromCode, fieldKey);
        return {active: true, oppositeToCode: opposite?.toCode ?? null};
    }
    const ownedKey = iaOwnedFieldKey(fromCode);
    if (detailHasOutboundIaOwnedFieldAdj(detail, ownedKey)) {
        const opposite = resolveIaAmorNbvOpposite(fromCode, ownedKey);
        return {active: true, oppositeToCode: opposite?.toCode ?? null};
    }
    if (isIaScheduleLevelNbvOrAmorContext(fromCode, detail)) {
        const upper = String(fromCode).trim().toUpperCase();
        const fieldKey = upper === 'PL-AE-AIA' ? 'amorChgAdj' : 'nbvCfAdj';
        const opposite = resolveIaAmorNbvOpposite(fromCode, fieldKey);
        return {active: true, oppositeToCode: opposite?.toCode ?? null};
    }
    return {active: false, oppositeToCode: null};
}

/** 仅 BS-EQ-IC；WP_TO_WP 下从 EXCLUSIVE_CODES 豁免，可直接选中叶子 */
function isIssuedCapitalFsCode(code?: string | null): boolean {
    return String(code || '').trim().toUpperCase() === 'BS-EQ-IC';
}

/** fromPaper is Depn↔NBV slim field adj — allow opposite PPE/DEP leaf (no category List). */
function readPpeDepnNbvCrossMode(): {
    active: boolean;
    oppositeToCode: string | null;
} {
    const draft = useAdjustDraftStore.getState();
    const fromCode = draft.fromPaper?.code || null;
    if (!fromCode) return {active: false, oppositeToCode: null};
    const detail = draft.getUnifiedDetail(fromCode);
    if (isPpeDepnNbvCrossFieldDetail(detail, fromCode)) {
        const fieldKey = getPpeDepnNbvCrossFieldKey(detail, fromCode);
        const opposite = resolvePpeDepnNbvOpposite(fromCode, fieldKey);
        return {active: true, oppositeToCode: opposite?.toCode ?? null};
    }
    // Grand Total NBV: empty details on BS-NCA-PPE → opposite PL-AE-DEP
    if (isPpeScheduleLevelNbvOrDepnContext(fromCode, detail)) {
        return {active: true, oppositeToCode: 'PL-AE-DEP'};
    }
    return {active: false, oppositeToCode: null};
}

function treeContainsPpeOrDep(nodes: AuditFsTreeSelection[]): boolean {
    for (const n of nodes) {
        if (isPpeOrDepFsCode(n.code)) return true;
        if (Array.isArray(n.children) && treeContainsPpeOrDep(n.children)) return true;
    }
    return false;
}

/** level=2 父节点默认不进入搜索选项；Taxation (PL-TAX) 为例外（本身即为可调 level=2） */
function isSearchableFsNode(n: AuditFsTreeSelection): boolean {
    if ((n as any).level !== 2) return true;
    const code = String(n.code || '').trim().toUpperCase();
    return code === 'PL-TAX';
}

/** WP_TO_WP 且无别名时，loan 节点的展示名称覆盖 */
const WP_TO_WP_LOAN_DISPLAY_NAMES: Record<string, string> = {
    'BS-CL-BLS': 'Bank loan, secured (current)',
    'BS-NCL-BLS': 'Bank loan, secured (non-current)',
    'BS-CL-OFL': 'Obligation under finance lease(current)',
    'BS-NCL-OFL': 'Obligation under finance lease(non-current)',
};

/** WP_TO_WP 下 PL-FC-LI 二级菜单：选后用对应 code 拉 WP，line code 仍为 PL-FC-LI */
const PL_FC_LI_INTEREST_SUBMENU = [
    {label: 'Bank loan, secured(interest)', wpFetchCode: 'BS-CL-BLS'},
    {label: 'Obligation under finance lease(interest)', wpFetchCode: 'BS-CL-OFL'},
] as const;

/** WP_TO_WP 下 PL-AE-MPF 二级菜单：选后用对应 code 拉 WP，line code 仍为 PL-AE-MPF */
const PL_AE_MPF_SUBMENU = [
    {label: "Director's remuneration (MPF)", wpFetchCode: 'PL-AE-DR'},
    {label: 'Salary Allowance (MPF)', wpFetchCode: 'PL-AE-SAL'},
    {label: 'Wages expense (MPF)', wpFetchCode: 'PL-AE-WAG'},
] as const;

const PL_FC_LI_CODE = 'PL-FC-LI';
const PL_AE_MPF_CODE = 'PL-AE-MPF';
const PL_AE_REN_CODE = 'PL-AE-REN';
const PPE_WP_CODE = 'BS-NCA-PPE';
const VEHICLE_LOAN_WP_FETCH_CODE = 'BS-CL-OFL';
/** Vehicle Loan 目标节点：BS-CL-OFL / BS-NCL-OFL / PL-FC-LI(vehicle interest) */
const VEHICLE_LOAN_FS_CODES = new Set(['BS-CL-OFL', 'BS-NCL-OFL']);

function isRentalExpenseFsCode(code: string, templateCodeRef?: string | null): boolean {
    const normalized = String(code || '').trim().toUpperCase();
    const normalizedRef = String(templateCodeRef || '').trim().toUpperCase();
    return normalized === PL_AE_REN_CODE || normalizedRef === PL_AE_REN_CODE;
}

const PPE_EMPTY_CONFIRM_MESSAGE =
    'No hire purchase motor data found in Property, Plant & Equipment. Would you like to go to PPE to fill in the data first?';

function isVehicleLoanAdjustmentTarget(code: string, wpFetchCode?: string): boolean {
    const normalized = String(code || '').trim().toUpperCase();
    if (VEHICLE_LOAN_FS_CODES.has(normalized)) return true;
    if (
        normalized === PL_FC_LI_CODE
        && String(wpFetchCode || '').trim().toUpperCase() === VEHICLE_LOAN_WP_FETCH_CODE
    ) {
        return true;
    }
    return false;
}

/** 仅 WP→WP 选中 Vehicle Loan 目标时才做 PPE 空数据拦截 */
function shouldGuardVehicleLoanPpe(
    adjustmentMode: AdjustmentMode | undefined,
    code: string,
    wpFetchCode?: string,
): boolean {
    if (adjustmentMode !== AdjustmentMode.ADJUST_WP_TO_WP) return false;
    return isVehicleLoanAdjustmentTarget(code, wpFetchCode);
}

async function hasHirePurchaseMotorInPpe(companyId: number, annualId: number): Promise<boolean> {
    if (!companyId || !annualId) return true;
    try {
        const response = await getWorkingPaper({
            companyId,
            annualId,
            code: PPE_WP_CODE,
        });
        const detail = Array.isArray(response) && response.length > 0 ? response[0] : null;
        const hirePurchaseMotorRow = (detail as any)?.hirePurchaseMotorRow;
        return Array.isArray(hirePurchaseMotorRow) && hirePurchaseMotorRow.length > 0;
    } catch {
        // PPE 拉取失败时不阻断选中，避免误伤正常 adjustment 流程
        return true;
    }
}

/**
 * WP_TO_WP 打开 Drawer 时拉取 BS-NCA-PPE 标题（含自定义名）构建分类二级菜单。
 * 仅用 getWorkingPaper，不写入共享 wpDetailsCache，避免影响其它组件缓存语义。
 */
async function loadPpeCategoryOptionsForDrawer(
    companyId: number,
    annualId: number,
): Promise<PpeCategoryOption[]> {
    let ppeTitle: Record<string, string> | null = null;
    if (companyId && annualId) {
        try {
            const response = await getWorkingPaper({
                companyId,
                annualId,
                code: PPE_WP_CODE,
            });
            const detail = Array.isArray(response) && response.length > 0 ? response[0] : null;
            const rawTitle = (detail as any)?.ppeTitle;
            if (rawTitle && typeof rawTitle === 'object') {
                ppeTitle = rawTitle as Record<string, string>;
            }
        } catch {
            // 拉取失败时仍用默认分类名，不阻断选择
        }
    }
    return buildPpeCategoryOptions(ppeTitle);
}

/**
 * 仅 PPE/DEP 分类选中时：把 BS-NCA-PPE details 写入 fsAdjustStore.wpDetailsCache（对齐 REN）。
 * 不改动 Vehicle Loan / 其它选中路径。
 */
async function ensurePpeWpDetailCached(companyId: number, annualId: number): Promise<void> {
    if (!companyId || !annualId) return;
    try {
        await useFsAdjustStore.getState().getWpDetail(companyId, annualId, PPE_WP_CODE);
    } catch {
        // 缓存失败不阻断选中；assembly 会再拉一次或走空分类回退
    }
}

function isPlFcLiInterestExpandable(
    node: AuditFsTreeSelection,
    adjustmentMode?: AdjustmentMode,
): boolean {
    if (adjustmentMode !== AdjustmentMode.ADJUST_WP_TO_WP) return false;
    return String(node.code || '').trim().toUpperCase() === PL_FC_LI_CODE;
}

function isPlAeMpfExpandable(
    node: AuditFsTreeSelection,
    adjustmentMode?: AdjustmentMode,
): boolean {
    if (adjustmentMode !== AdjustmentMode.ADJUST_WP_TO_WP) return false;
    return String(node.code || '').trim().toUpperCase() === PL_AE_MPF_CODE;
}

/**
 * 其它 paper-registry 组件发起 adjustment 时，PropertyPlantEquipment（BS-NCA-PPE）
 * 只选根节点，不展开分类 List。PPE/DEP 自身发起仍可走分类二级菜单。
 */
function isInboundPpeRootOnlySelection(fromCode?: string | null, toCode?: string | null): boolean {
    const toUpper = String(toCode || '').trim().toUpperCase();
    if (toUpper !== PPE_WP_CODE) return false;
    return !isPpeOrDepFsCode(fromCode);
}

/** WP_TO_WP 下 PPE / Depreciation：仅展开二级分类菜单。
 * Depn↔NBV / schedule-level Grand Total NBV / ITL Depn↔NBV：直接选中叶子，不展开分类 List。
 * 其它组件 → BS-NCA-PPE：仅选根节点（无分类 List）。 */
function isPpeOrDepExpandable(
    node: AuditFsTreeSelection,
    adjustmentMode?: AdjustmentMode,
): boolean {
    if (adjustmentMode !== AdjustmentMode.ADJUST_WP_TO_WP) return false;
    const nodeCode = String(node.code || '').trim().toUpperCase();
    if (!isPpeOrDepFsCode(nodeCode)) return false;
    if (readPpeDepnNbvCrossMode().active) return false;
    // ITL NBV → DEP (or reverse): allow leaf click without PPE category List
    const draft = useAdjustDraftStore.getState();
    const fromCode = draft.fromPaper?.code || null;
    // 其它 WP 发起 → PropertyPlantEquipment 根节点可选，取消 xxxRow 分类 List
    if (isInboundPpeRootOnlySelection(fromCode, nodeCode)) return false;
    if (fromCode) {
        const detail = draft.getUnifiedDetail(fromCode);
        if (isIaAmorNbvCrossFieldDetail(detail, fromCode)) {
            const fieldKey = getIaAmorNbvCrossFieldKey(detail, fromCode);
            const opposite = resolveIaAmorNbvOpposite(fromCode, fieldKey);
            if (opposite?.toCode === nodeCode) {
                return false;
            }
        }
        // Schedule-level IA/ITL Total NBV (empty details) → opposite leaf only
        if (isIaScheduleLevelNbvOrAmorContext(fromCode, detail)) {
            const upper = String(fromCode).trim().toUpperCase();
            const fieldKey = upper === 'PL-AE-AIA' ? 'amorChgAdj' : 'nbvCfAdj';
            const opposite = resolveIaAmorNbvOpposite(fromCode, fieldKey);
            if (opposite?.toCode === nodeCode) {
                return false;
            }
        }
    }
    return true;
}

/** WP_TO_WP 下仅展开二级菜单、不可直接选中的父节点（PL-AE-REN 已改为直接选叶子） */
function isWpToWpSubmenuParent(
    node: AuditFsTreeSelection,
    adjustmentMode?: AdjustmentMode,
): boolean {
    return isPlFcLiInterestExpandable(node, adjustmentMode)
        || isPlAeMpfExpandable(node, adjustmentMode)
        || isPpeOrDepExpandable(node, adjustmentMode);
}

function resolveFsSelectionDisplayName(
    node: AuditFsTreeSelection,
    adjustmentMode?: AdjustmentMode,
): string {
    const alias = String((node as any).aliasEn || (node as any).alias || '').trim();
    if (alias) return alias;

    if (adjustmentMode === AdjustmentMode.ADJUST_WP_TO_WP) {
        const code = String(node.code || '').trim().toUpperCase();
        const override = WP_TO_WP_LOAN_DISPLAY_NAMES[code];
        if (override) return override;
    }

    return String((node as any).nameEn || node.name || '');
}

export type FsSelectionDialogProps = {
    open: boolean;
    onClose: () => void;
    payload?: Partial<Record<string, any>> & {
        companyId?: number;
        annualId?: number;
        title?: string;
    };
    onConfirm?: (args: {
        code: string;
        node: AuditFsTreeSelection;
        payload: Record<string, any>;
        /** 二级菜单：实际拉取 WP 的 code / discriminator（PL-FC-LI→loan / PL-AE-MPF→DR|SAL|WAG / PPE→xxxRow） */
        wpFetchCode?: string;
    }) => void;
    excludeFsIds?: number[];
    excludeFilter?: (node: AuditFsTreeSelection) => boolean;
    /** 调整模式：WP_TO_WP / FS_TO_FS，用于控制节点过滤逻辑 */
    adjustmentMode?: AdjustmentMode;
};

function NodeRow({node, depth, onLaunch, onChipClick, forceOpen, adjustmentMode, ppeCategoryOptions}: {
    node: AuditFsTreeSelection;
    depth: number;
    onLaunch: (code: string, node: AuditFsTreeSelection) => void;
    onChipClick?: (code: string, node?: AuditFsTreeSelection, wpFetchCode?: string) => void;
    forceOpen?: boolean;
    adjustmentMode?: AdjustmentMode;
    /** WP_TO_WP PPE/DEP 二级分类（含自定义 title） */
    ppeCategoryOptions?: PpeCategoryOption[];
}) {
    const [open, setOpen] = useState<boolean>(false);
    const isInterestParent = isPlFcLiInterestExpandable(node, adjustmentMode);
    const isMpfParent = isPlAeMpfExpandable(node, adjustmentMode);
    const isPpeParent = isPpeOrDepExpandable(node, adjustmentMode);
    const isSubmenuParent = isWpToWpSubmenuParent(node, adjustmentMode);
    const ppeSubmenu = ppeCategoryOptions ?? buildPpeCategoryOptions(null);

    // 如果 forceOpen 为 true，强制展开
    React.useEffect(() => {
        if (forceOpen) {
            setOpen(true);
        }
    }, [forceOpen]);
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const showExpandToggle = hasChildren || isSubmenuParent;
    const indent = depth * 2;
    // Level=2 nodes are parent nodes, only expandable, not selectable
    const isParentNode = (node as any).level === 2;
    // PL-FC-LI / PL-AE-MPF / PPE|DEP in WP_TO_WP：只展开二级菜单，不直接选中；PL-AE-REN 直接选叶子
    const isClickable = !!node.code && !isParentNode && !isSubmenuParent;

    // 有别名用别名；WP_TO_WP 下无别名时对 loan 节点使用覆盖名称
    const displayName = resolveFsSelectionDisplayName(node, adjustmentMode);

    // 點擊整行選中節點；僅 WP_TO_WP 二级菜单父節點允許整行展開，其餘 Level=2 仍只靠 IconButton
    const handleRowClick = () => {
        if (isSubmenuParent && showExpandToggle) {
            setOpen(o => !o);
            return;
        }
        if (!isClickable) return;
        if (onChipClick) {
            onChipClick(node.code!, node);
        } else {
            onLaunch(node.code!, node);
        }
    };

    return (
        <>
            <ListItem
                disableGutters
                sx={{
                    py: 0.25,
                    cursor: (isClickable || isSubmenuParent) ? 'pointer' : 'default',
                    '&:hover': (isClickable || isSubmenuParent)
                        ? {backgroundColor: 'action.hover'}
                        : undefined,
                }}
                data-fsid={node.fsId}
                onClick={isClickable || isSubmenuParent ? handleRowClick : undefined}
            >
                <Box sx={{width: '100%'}}>
                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{pl: indent}}>
                        {showExpandToggle ? (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(o => !o);
                                }}
                                sx={{p: 0.25}}
                            >
                                {open ? <ExpandLess fontSize="small"/> : <ExpandMore fontSize="small"/>}
                            </IconButton>
                        ) : (
                            <Box sx={{width: 24}}/>
                        )}
                        <Box sx={{flex: 1, minWidth: 0, display: 'flex', alignItems: 'center'}}>
                            <Typography
                                variant="body2"
                                noWrap
                                title={displayName}
                                sx={{fontWeight: 600, minWidth: 0}}
                            >
                                {displayName}
                            </Typography>
                        </Box>
                        {!!node.scheduleName && (
                            <Chip
                                size="small"
                                variant="outlined"
                                color={isClickable ? 'primary' : 'default'}
                                label={node.scheduleName}
                                title={node.scheduleName}
                                sx={{ml: 1, flexShrink: 0}}
                            />
                        )}
                    </Stack>
                </Box>
            </ListItem>
            {showExpandToggle && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List disablePadding>
                        {isInterestParent && PL_FC_LI_INTEREST_SUBMENU.map((opt) => (
                            <ListItem
                                key={opt.wpFetchCode}
                                disableGutters
                                sx={{
                                    py: 0.25,
                                    cursor: 'pointer',
                                    '&:hover': {backgroundColor: 'action.hover'},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onChipClick) {
                                        onChipClick(PL_FC_LI_CODE, node, opt.wpFetchCode);
                                    } else {
                                        onLaunch(PL_FC_LI_CODE, {
                                            ...node,
                                            code: PL_FC_LI_CODE,
                                        });
                                    }
                                }}
                            >
                                <Box sx={{width: '100%'}}>
                                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{pl: (depth + 1) * 2}}>
                                        <Box sx={{width: 24}}/>
                                        <Typography variant="body2" noWrap title={opt.label} sx={{fontWeight: 600}}>
                                            {opt.label}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </ListItem>
                        ))}
                        {isMpfParent && PL_AE_MPF_SUBMENU.map((opt) => (
                            <ListItem
                                key={opt.wpFetchCode}
                                disableGutters
                                sx={{
                                    py: 0.25,
                                    cursor: 'pointer',
                                    '&:hover': {backgroundColor: 'action.hover'},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onChipClick) {
                                        onChipClick(PL_AE_MPF_CODE, node, opt.wpFetchCode);
                                    } else {
                                        onLaunch(PL_AE_MPF_CODE, {
                                            ...node,
                                            code: PL_AE_MPF_CODE,
                                        });
                                    }
                                }}
                            >
                                <Box sx={{width: '100%'}}>
                                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{pl: (depth + 1) * 2}}>
                                        <Box sx={{width: 24}}/>
                                        <Typography variant="body2" noWrap title={opt.label} sx={{fontWeight: 600}}>
                                            {opt.label}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </ListItem>
                        ))}
                        {isPpeParent && ppeSubmenu.map((opt) => (
                            <ListItem
                                key={opt.key}
                                disableGutters
                                sx={{
                                    py: 0.25,
                                    cursor: 'pointer',
                                    '&:hover': {backgroundColor: 'action.hover'},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const parentCode = String(node.code || '').trim().toUpperCase();
                                    if (onChipClick) {
                                        onChipClick(parentCode, node, opt.key);
                                    } else {
                                        onLaunch(parentCode, {
                                            ...node,
                                            code: parentCode,
                                        });
                                    }
                                }}
                            >
                                <Box sx={{width: '100%'}}>
                                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{pl: (depth + 1) * 2}}>
                                        <Box sx={{width: 24}}/>
                                        <Typography variant="body2" noWrap title={opt.label} sx={{fontWeight: 600}}>
                                            {opt.label}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </ListItem>
                        ))}
                        {isPpeParent && String(node.code || '').trim().toUpperCase() === 'PL-AE-DEP' && (
                            <ListItem
                                key={PL_AE_DEP_TAXI_LICENSE_OPTION.key}
                                disableGutters
                                sx={{
                                    py: 0.25,
                                    cursor: 'pointer',
                                    '&:hover': {backgroundColor: 'action.hover'},
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const parentCode = 'PL-AE-DEP';
                                    if (onChipClick) {
                                        onChipClick(parentCode, node, PL_AE_DEP_TAXI_LICENSE_OPTION.key);
                                    } else {
                                        onLaunch(parentCode, {
                                            ...node,
                                            code: parentCode,
                                        });
                                    }
                                }}
                            >
                                <Box sx={{width: '100%'}}>
                                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{pl: (depth + 1) * 2}}>
                                        <Box sx={{width: 24}}/>
                                        <Typography variant="body2" noWrap title={PL_AE_DEP_TAXI_LICENSE_OPTION.label} sx={{fontWeight: 600}}>
                                            {PL_AE_DEP_TAXI_LICENSE_OPTION.label}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </ListItem>
                        )}
                        {hasChildren && node.children!.map((child, idx) => (
                            <NodeRow
                                key={`${child.fsId}-${idx}`}
                                node={child}
                                depth={depth + 1}
                                onLaunch={onLaunch}
                                onChipClick={onChipClick}
                                forceOpen={forceOpen}
                                adjustmentMode={adjustmentMode}
                                ppeCategoryOptions={ppeCategoryOptions}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
}

function FsSelectionDrawerComponent({
                                        open,
                                        onClose,
                                        payload,
                                        onConfirm,
                                        excludeFsIds,
                                        excludeFilter,
                                        adjustmentMode
                                    }: FsSelectionDialogProps) {
    const companyId = useMemo(() => Number(payload?.companyId ?? 0), [payload?.companyId]);
    const annualId = useMemo(() => Number(payload?.annualId ?? 0), [payload?.annualId]);
    const currentFsId = useMemo(() => Number(payload?.fsId ?? 0), [payload?.fsId]);
    const getFsOriginal = useMemo(() => (payload as any)?.getFsOriginal as (undefined | ((id: number) => {
        amount?: number;
        finalAmount?: number
    } | null)), [payload]);

    const rawBsData = useFsAdjustStore(state => state.adjustRecord?.bsLine || null);
    const rawPlDataTop = useFsAdjustStore(state => state.adjustRecord?.plLine || null);
    const adjustPeriods = useFsAdjustStore(state => state.adjustRecord?.periods || null);

    const periodLock = useAdjustDraftStore(state => state.periodLock);
    const periodLockIsAppend = useAdjustDraftStore(state => state.periodLockIsAppend);
    const setPeriodLockAnchor = useAdjustDraftStore(state => state.setPeriodLockAnchor);

    const fromPaperCode = useAdjustDraftStore(state => state.fromPaper?.code || state.fromPaper?.code);
    const fromPaperAdjLine = useAdjustDraftStore(state => state.fromPaper?.adjustmentLine);
    const toPaperCodes = useAdjustDraftStore(state => state.toPaper?.codes);

    const adjustDraftStore = useAdjustDraftStore();

    // Phase 5 §5.7: period selector in Drawer (MULTI only)
    const [drawerSliceKey, setDrawerSliceKey] = useState<string>('');
    const [periodLockSnack, setPeriodLockSnack] = useState<string | null>(null);

    const periodLockCtx: AdjPeriodLockContext | null = useMemo(() => {
        if (!periodLock || periodLock.spanType !== 'MULTI' || !periodLock.periods?.length) return null;
        return {
            spanType: 'MULTI',
            initiator: periodLock.initiator,
            anchorSliceKey: periodLock.anchorSliceKey,
            isAppend: !!periodLockIsAppend,
            periods: periodLock.periods as AuditPeriodSlice[],
        };
    }, [periodLock, periodLockIsAppend]);

    const allSliceKeys = useMemo(
        () => (periodLockCtx?.periods || []).map((p) => p.key),
        [periodLockCtx],
    );

    const showDrawerYoaSelector = useMemo(
        () => (periodLockCtx ? shouldShowDrawerYoaSelector(periodLockCtx) : false),
        [periodLockCtx],
    );

    useEffect(() => {
        if (!open || !periodLockCtx) {
            setDrawerSliceKey('');
            return;
        }
        // Silent YOA: initiator / existing anchor (no UI). Do not default periods[0].
        const silentKey = resolveDrawerEffectiveSliceKey(periodLockCtx, null);
        if (silentKey && periodLockCtx.periods.some((p) => p.key === silentKey)) {
            setDrawerSliceKey(silentKey);
            return;
        }
        if (showDrawerYoaSelector) {
            // BS non-multi first open: leave empty until user picks (required only for PL / BS multi)
            setDrawerSliceKey('');
            return;
        }
        setDrawerSliceKey('');
    }, [open, periodLockCtx, showDrawerYoaSelector]);

    // Prefer period-scoped PL tree when MULTI + slice selected
    const rawPlData = useMemo(() => {
        if (!periodLockCtx || !drawerSliceKey || !Array.isArray(adjustPeriods)) {
            return rawPlDataTop;
        }
        const match = adjustPeriods.find((p: any) => p.key === drawerSliceKey);
        if (match?.plLine && Array.isArray(match.plLine) && match.plLine.length > 0) {
            return match.plLine;
        }
        return rawPlDataTop;
    }, [periodLockCtx, drawerSliceKey, adjustPeriods, rawPlDataTop]);

    const ppeDepnNbvCross = useMemo(() => {
        if (!open || !fromPaperCode) return {active: false, oppositeToCode: null as string | null};
        const detail = adjustDraftStore.getUnifiedDetail(fromPaperCode);
        if (!isPpeDepnNbvCrossFieldDetail(detail, fromPaperCode)) {
            return {active: false, oppositeToCode: null};
        }
        const fieldKey = getPpeDepnNbvCrossFieldKey(detail, fromPaperCode);
        const opposite = resolvePpeDepnNbvOpposite(fromPaperCode, fieldKey);
        return {active: true, oppositeToCode: opposite?.toCode ?? null};
        // fromPaperAdjLine: re-evaluate when fromPaper details land
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fromPaperCode, fromPaperAdjLine, adjustDraftStore]);

    const iaAmorNbvCross = useMemo(() => {
        if (!open || !fromPaperCode) return {active: false, oppositeToCode: null as string | null};
        const detail = adjustDraftStore.getUnifiedDetail(fromPaperCode);
        return resolveIaAmorNbvCrossForDrawer(fromPaperCode, detail);
        // fromPaperAdjLine: re-evaluate when fromPaper details land
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fromPaperCode, fromPaperAdjLine, adjustDraftStore]);

    const [activeTab, setActiveTab] = useState<'bs' | 'pl'>('bs');

    const [launchCode, setLaunchCode] = useState<string | null>(null);
    const [launchPayload, setLaunchPayload] = useState<Record<string, any>>({});
    const [launchOpen, setLaunchOpen] = useState<boolean>(false);

    // Vehicle Loan → PPE hire-purchase 为空时的确认框（与打开 BS-CL-OFL / BS-NCL-OFL 一致）
    const [ppeEmptyConfirmOpen, setPpeEmptyConfirmOpen] = useState(false);
    const [ppeCheckLoading, setPpeCheckLoading] = useState(false);
    const ppeCheckInFlightRef = useRef(false);
    // PPE/DEP 分类选中预取：独立于 Vehicle Loan 校验，互不抢占
    const [ppeCategoryCacheLoading, setPpeCategoryCacheLoading] = useState(false);
    const ppeCategoryCacheInFlightRef = useRef(false);
    const requestOpenWpCode = useAdjustDraftStore((s) => s.requestOpenWpCode);

    // WP_TO_WP → PPE / Depreciation 二级分类（Drawer 打开时拉取自定义 title）
    const [ppeCategoryOptions, setPpeCategoryOptions] = useState<PpeCategoryOption[]>(() =>
        buildPpeCategoryOptions(null),
    );

    // Search state
    const [searchInput, setSearchInput] = useState<string>('');
    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

    // ✅ 使用 ref 缓存 excludeFsIds，避免数组引用变化导致重新计算
    const excludeFsIdsRef = useRef<number[]>([]);
    useEffect(() => {
        if (excludeFsIds) {
            excludeFsIdsRef.current = excludeFsIds;
        }
    }, [excludeFsIds]);

    const excludeFsIdsKey = useMemo(() => {
        return excludeFsIds?.join(',') || '';
    }, [excludeFsIds]);

    // ✅ 稳定化 toPaperCodes key（使用 sorted 确保一致性）
    const toPaperCodesKey = useMemo(() => {
        if (!toPaperCodes || toPaperCodes.length === 0) return '';
        return [...toPaperCodes].sort().join(',');
    }, [toPaperCodes]);

    // 使用 useMemo 计算过滤后的树，避免 useEffect 导致的无限循环
    const {bsTree, plTree} = useMemo(() => {
        // 只要有一個數據就可以渲染，不需要兩個都有
        if (!open || (!rawBsData && !rawPlData)) {
            return {bsTree: [], plTree: []};
        }

        // 直接计算使用的 codes，避免依赖外部变量
        const codes: string[] = [];
        if (fromPaperCode) codes.push(fromPaperCode);
        if (toPaperCodes) codes.push(...toPaperCodes);
        const usedCodesSet = new Set(codes.map(code => code.toUpperCase()));

        const bsData = rawBsData;
        const plData = rawPlData;
        const filterFn = excludeFilter; // 捕获到局部变量，避免闭包问题
        const localExcludeFsIds = excludeFsIdsRef.current; // ✅ 从 ref 读取
        const mode = adjustmentMode; // 捕获到局部变量

        // 過濾函數：排除指定的 fsId 和已使用的 code
        const filterOut = (nodes: any[]): AuditFsTreeSelection[] => {
            if (!Array.isArray(nodes)) return [];
            return nodes
                .filter(n => {
                    const inExcluded = localExcludeFsIds.includes(Number(n.fsId));
                    const isCurrent = !!currentFsId && n.fsId === currentFsId;
                    const excludedByPredicate = typeof filterFn === 'function' ? filterFn(n) : false;

                    // 檢查 code 是否已被使用
                    const code = String(n.code || '').trim().toUpperCase();
                    const isCodeUsed = code && usedCodesSet.has(code);
                    const templateCodeRef = (n as { templateCodeRef?: string | null }).templateCodeRef;

                    // 根據 adjustmentMode 決定過濾邏輯
                    const isLeafNode = !!n.scheduleName;
                    const isLoanFieldCode = isLoanFieldFsCode(code)
                        || isLoanFieldFsCode(templateCodeRef);
                    let isExcludedByMode = false;

                    // Rental expense (PL-AE-REN)：WP_TO_WP 可选；FS_TO_FS 隐藏
                    const isRentalExpenseCode = isRentalExpenseFsCode(code, templateCodeRef);
                    // PPE / Depreciation：WP_TO_WP 可选（仍受互斥组约束）
                    const isPpeOrDepCode = isPpeOrDepFsCode(code) || isPpeOrDepFsCode(templateCodeRef);
                    // Intangible / Amortization：仅 WP_TO_WP 从 EXCLUSIVE 豁免为可选叶子（仍受互斥组约束）
                    const isIaOrAiaCode = isIaOrAiaFsCode(code) || isIaOrAiaFsCode(templateCodeRef);
                    // Issued Capital：仅 WP_TO_WP 从 EXCLUSIVE 豁免为可选叶子
                    const isIssuedCapitalCode = isIssuedCapitalFsCode(code) || isIssuedCapitalFsCode(templateCodeRef);

                    // Loan 字段节点：WP_TO_WP 显示；FS_TO_FS 隐藏
                    if (isLoanFieldCode && mode === AdjustmentMode.ADJUST_FS_TO_FS) {
                        isExcludedByMode = true;
                    } else if (isRentalExpenseCode && mode === AdjustmentMode.ADJUST_FS_TO_FS) {
                        isExcludedByMode = true;
                    } else if (mode === AdjustmentMode.ADJUST_WP_TO_WP) {
                        // WP_TO_WP 模式：排除 EXCLUSIVE_CODES，但保留 loan / rental / PPE|DEP / IA|AIA / IC 节点可选
                        if (code && isLeafNode) {
                            isExcludedByMode = EXCLUSIVE_CODES.has(code)
                                && !isLoanFieldCode
                                && !isRentalExpenseCode
                                && !isPpeOrDepCode
                                && !isIaOrAiaCode
                                && !isIssuedCapitalCode;
                        }
                    }

                    // 全局隐藏：如 MPF contributions (PL-AE-MPF)；WP_TO_WP 下改为二级菜单可选
                    const isAlwaysHidden = isAlwaysHiddenFsSelectionCode(code, templateCodeRef)
                        && !(mode === AdjustmentMode.ADJUST_WP_TO_WP && code === PL_AE_MPF_CODE);

                    // 互斥组过滤：fromPaper 为组内一侧时，禁止选择同组对侧作为 toPaper
                    // Depn↔NBV：允许对侧 PPE/DEP 叶子；Amor↔NBV field adj：允许对侧 IA/AIA 叶子
                    const isCrossExcluded = isCrossExclusiveAdjustmentTarget(fromPaperCode, {
                        code,
                        templateCodeRef,
                    });
                    const targetUpper = String(code || '').trim().toUpperCase();
                    const templateUpper = String(templateCodeRef || '').trim().toUpperCase();
                    let oppositeAllowed = false;
                    if (ppeDepnNbvCross.active && ppeDepnNbvCross.oppositeToCode) {
                        oppositeAllowed = targetUpper === ppeDepnNbvCross.oppositeToCode
                            || templateUpper === ppeDepnNbvCross.oppositeToCode;
                    } else if (iaAmorNbvCross.active && iaAmorNbvCross.oppositeToCode) {
                        oppositeAllowed = targetUpper === iaAmorNbvCross.oppositeToCode
                            || templateUpper === iaAmorNbvCross.oppositeToCode;
                    }
                    const effectivelyCrossExcluded = isCrossExcluded && !oppositeAllowed;

                    // TaxWp 发起：按 from 行隐藏同组其余 Tax 节点（见 getHiddenTaxFsCodesForTaxWpFromPaper）
                    const isTaxSiblingHidden = isTaxWpHiddenFsSelectionTarget(fromPaperCode, code)
                        || isTaxWpHiddenFsSelectionTarget(
                            fromPaperCode,
                            templateCodeRef ? String(templateCodeRef) : null,
                        );

                    return !inExcluded && !isCurrent && !excludedByPredicate && !isCodeUsed
                        && !isExcludedByMode && !isAlwaysHidden && !effectivelyCrossExcluded && !isTaxSiblingHidden;
                })
                .map(n => ({
                    ...n,
                    entry: n.entry || null, // 确保包含 entry 字段
                    children: Array.isArray(n.children) ? filterOut(n.children) : [],
                }))
                .filter(n => {
                    // 移除沒有 scheduleName 且沒有子節點的父級菜單
                    const hasScheduleName = !!n.scheduleName;
                    const hasChildren = Array.isArray(n.children) && n.children.length > 0;
                    const nodeCode = String(n.code || '').trim().toUpperCase();
                    

                    // WP_TO_WP：二级菜单父节点即使无 children / scheduleName 也保留
                    // Amor↔NBV field adj：仅保留互斥豁免的对端叶子（NBV→AIA / Amor→IA），不扩大到全部 IA/AIA
                    if (
                        mode === AdjustmentMode.ADJUST_WP_TO_WP
                        && (
                            nodeCode === PL_AE_MPF_CODE
                            || nodeCode === PL_AE_REN_CODE
                            || isPpeOrDepFsCode(nodeCode)
                            || (
                                iaAmorNbvCross.active
                                && !!iaAmorNbvCross.oppositeToCode
                                && nodeCode === iaAmorNbvCross.oppositeToCode
                            )
                        )
                    ) {
                        return true;
                    }
                    
                    return hasScheduleName || hasChildren;
                });
        };

        const filteredBsTree = bsData ? filterOut(bsData) : [];
        const filteredPlTree = plData ? filterOut(plData) : [];

        return {
            bsTree: filteredBsTree,
            plTree: filteredPlTree
        };
    }, [open, rawBsData, rawPlData, fromPaperCode, toPaperCodesKey, currentFsId, excludeFsIdsKey, excludeFilter, adjustmentMode, ppeDepnNbvCross, iaAmorNbvCross]);

    // 计算 loading 状态：如果弹窗打开但还没有数据，则显示 loading
    const loading = open && (!rawBsData && !rawPlData);

    // 计算 error 状态
    const error = open && (!companyId || !annualId) ? '缺少 companyId 或 annualId' : null;

    const finalizeChipSelection = useCallback((code: string, nodeWithEntry?: AuditFsTreeSelection, wpFetchCode?: string) => {
        // Phase 5 §5.7 period lock validation (+ YOA column-gap rules)
        let periodPayloadExtra: Record<string, unknown> = {};
        if (periodLockCtx) {
            const targetKind = classifyAdjTargetKind(code, nodeWithEntry?.code?.startsWith('PL') ? 'PL' : undefined);
            const effectiveKey = resolveDrawerEffectiveSliceKey(periodLockCtx, drawerSliceKey);
            const needsYoa = shouldRequireDrawerYoa(periodLockCtx, targetKind);

            if (needsYoa && !effectiveKey) {
                setPeriodLockSnack('Please select a period first (anchor year required).');
                return;
            }

            const allowed = resolveAllowedSliceKeys(periodLockCtx, targetKind, allSliceKeys);
            if (allowed !== 'ANY') {
                if (!effectiveKey || !allowed.includes(effectiveKey)) {
                    setPeriodLockSnack(
                        allowed.length === 0
                            ? 'Please select a period first (anchor year required).'
                            : `Only period ${allowed.join(', ')} is allowed for this target.`,
                    );
                    return;
                }
            }

            // First select from BS non-multi onto PL / BS multi → write anchor
            if (
                needsYoa
                && !periodLockCtx.anchorSliceKey
                && !periodLockCtx.initiator.sliceKey
                && effectiveKey
            ) {
                setPeriodLockAnchor(effectiveKey);
            }

            if (shouldAttachDrawerYoaPayload(periodLockCtx, targetKind) && effectiveKey) {
                const periodSlice = periodLockCtx.periods?.find((p) => p.key === effectiveKey);
                if (periodSlice) {
                    periodPayloadExtra = {
                        sliceKey: periodSlice.key,
                        yearOfAssessment: periodSlice.yearOfAssessment,
                        periodFrom: periodSlice.periodFrom,
                        periodTo: periodSlice.periodTo,
                        periodScope: {
                            periodFrom: periodSlice.periodFrom,
                            periodTo: periodSlice.periodTo,
                            yearOfAssessment: periodSlice.yearOfAssessment,
                            sliceKey: periodSlice.key,
                        },
                    };
                }
            }
        }

        // WP→WP：PL-COT-OI/CI → BS-CA-INV
        const resolvedCode = resolveInventoriesToPaperCode(code) || code;
        const remappedToInv = normalizeFsCode(code) !== resolvedCode
            && resolvedCode === INV_ADJUSTMENT_CODE;
        const invLine = remappedToInv
            ? useFsAdjustStore.getState().getLineByCode(INV_ADJUSTMENT_CODE)
            : null;

        if (onConfirm) {
            // 优先使用传入的节点，确保包含完整信息（fsId, entry, scheduleName 等）
            const baseNode: AuditFsTreeSelection = nodeWithEntry
                ? {
                    ...nodeWithEntry,
                    code: nodeWithEntry.code || code,
                } : {
                    fsId: 0,
                    name: '',
                    alias: '',
                    code: '',
                    scheduleName: '',
                    entry: null,
                    children: [],
                };
            const node: AuditFsTreeSelection = remappedToInv
                ? {
                    ...baseNode,
                    code: INV_ADJUSTMENT_CODE,
                    fsId: invLine?.fsId ?? baseNode.fsId ?? 0,
                    name: invLine?.name || invLine?.scheduleName || baseNode.name || '',
                    alias: (invLine as {alias?: string} | null)?.alias || baseNode.alias || '',
                    scheduleName: invLine?.scheduleName || baseNode.scheduleName || '',
                    entry: invLine?.entry ?? baseNode.entry ?? null,
                }
                : baseNode;
            const confirmPayload = {
                ...(payload || {}),
                ...periodPayloadExtra,
                ...(wpFetchCode ? {wpFetchCode} : {}),
            };
            onConfirm({
                code: resolvedCode,
                node,
                payload: confirmPayload,
                ...(wpFetchCode ? {wpFetchCode} : {}),
            });
        } else {
            // Default behavior: replace toPaper (for standalone usage from CommonWorkingPaper)
            adjustDraftStore.setToPaper(resolvedCode, undefined, wpFetchCode
                ? {wpFetchCodes: {[resolvedCode]: wpFetchCode}}
                : undefined);
        }
        onClose?.();
    }, [
        onConfirm, onClose, payload, adjustDraftStore, adjustmentMode,
        periodLockCtx, allSliceKeys, drawerSliceKey, setPeriodLockAnchor,
    ]);

    const handleChipClick = useCallback((
        code: string,
        nodeWithEntry?: AuditFsTreeSelection,
        wpFetchCode?: string,
    ) => {
        // PPE/DEP 分类节点：先将 BS-NCA-PPE details 写入 fsAdjustStore（对齐 REN getWpDetail），
        // assembly 再据此判断是否 rowSelection 并写入 adjustDraftStore.toPaper.details
        const isPpeCategorySelect = isPpeOrDepFsCode(code) && isPpeCategoryFetchCode(wpFetchCode);
        if (isPpeCategorySelect) {
            if (ppeCategoryCacheInFlightRef.current) return;
            ppeCategoryCacheInFlightRef.current = true;
            setPpeCategoryCacheLoading(true);
            void (async () => {
                try {
                    await ensurePpeWpDetailCached(companyId, annualId);
                    finalizeChipSelection(code, nodeWithEntry, wpFetchCode);
                } finally {
                    ppeCategoryCacheInFlightRef.current = false;
                    setPpeCategoryCacheLoading(false);
                }
            })();
            return;
        }

        // 仅 WP→WP + Vehicle Loan 目标：先校验 PPE；其余路径保持原同步确认逻辑
        if (!shouldGuardVehicleLoanPpe(adjustmentMode, code, wpFetchCode)) {
            finalizeChipSelection(code, nodeWithEntry, wpFetchCode);
            return;
        }

        if (ppeCheckInFlightRef.current) return;
        ppeCheckInFlightRef.current = true;
        setPpeCheckLoading(true);

        void (async () => {
            try {
                const hasPpeData = await hasHirePurchaseMotorInPpe(companyId, annualId);
                if (!hasPpeData) {
                    setPpeEmptyConfirmOpen(true);
                    return;
                }
                finalizeChipSelection(code, nodeWithEntry, wpFetchCode);
            } finally {
                ppeCheckInFlightRef.current = false;
                setPpeCheckLoading(false);
            }
        })();
    }, [adjustmentMode, annualId, companyId, finalizeChipSelection]);

    const handlePpeEmptyConfirm = useCallback(() => {
        setPpeEmptyConfirmOpen(false);
        onClose?.();
        requestOpenWpCode(PPE_WP_CODE);
    }, [onClose, requestOpenWpCode]);

    const handlePpeEmptyCancel = useCallback(() => {
        setPpeEmptyConfirmOpen(false);
    }, []);

    // WP_TO_WP：仅当过滤后的树含 PPE/DEP 时才拉自定义 title（其它 WP_TO_WP 路径零额外请求）
    useEffect(() => {
        if (!open || adjustmentMode !== AdjustmentMode.ADJUST_WP_TO_WP) {
            setPpeCategoryOptions(buildPpeCategoryOptions(null));
            return;
        }
        if (!treeContainsPpeOrDep(bsTree) && !treeContainsPpeOrDep(plTree)) {
            setPpeCategoryOptions(buildPpeCategoryOptions(null));
            return;
        }
        let cancelled = false;
        void (async () => {
            const options = await loadPpeCategoryOptionsForDrawer(companyId, annualId);
            if (!cancelled) setPpeCategoryOptions(options);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, adjustmentMode, companyId, annualId, bsTree, plTree]);

    // Drawer 关闭时重置 PPE 确认态 / 分类预取态（互不影响）
    useEffect(() => {
        if (!open) {
            setPpeEmptyConfirmOpen(false);
            setPpeCheckLoading(false);
            ppeCheckInFlightRef.current = false;
            setPpeCategoryCacheLoading(false);
            ppeCategoryCacheInFlightRef.current = false;
        }
    }, [open]);

    // ✅ 使用 useCallback 稳定化回调
    const handleLaunch = useCallback((code: string, node: AuditFsTreeSelection) => {
        const basePayload: Record<string, any> = {
            ...(payload || {}),
            fsId: node.fsId,
            itemName: node.name,
        };

        // If external resolver provided, enrich payload with amount/finalAmount
        let nextPayload = basePayload;
        if (typeof getFsOriginal === 'function') {
            try {
                const resolved = getFsOriginal(node.fsId);
                if (resolved && (typeof resolved.amount !== 'undefined' || typeof resolved.finalAmount !== 'undefined')) {
                    nextPayload = {
                        ...basePayload,
                        amount: resolved.amount,
                        finalAmount: resolved.finalAmount,
                    };
                }
            } catch {
                // Ignore resolution errors
            }
        }

        if (onConfirm) {
            onConfirm({code, node, payload: nextPayload});
            onClose?.();
            return;
        }

        setLaunchCode(code);
        setLaunchPayload(nextPayload);
        setLaunchOpen(true);
        onClose?.();
    }, [onConfirm, onClose, payload, getFsOriginal]);

    const handleChildClose = useCallback(() => {
        setLaunchOpen(false);
    }, []);

    // 收集所有可搜索的选项
    const searchOptions = useMemo(() => {
        const options: Array<{
            label: string;
            displayLabel: string;
            code: string;
            value: string;
            fsId: number;
            tab: 'bs' | 'pl';
            node: AuditFsTreeSelection;
            wpFetchCode?: string;
        }> = [];
        const nameTabCount = new Map<string, number>();
        const nameTabIndex = new Map<string, number>();

        // 第一次遍历：统计每个 name-tab 组合的出现次数
        const countNames = (nodes: AuditFsTreeSelection[], tab: 'bs' | 'pl') => {
            nodes.forEach(n => {
                if (isSearchableFsNode(n)) {
                    const codeUpper = String(n.code || '').trim().toUpperCase();
                    if (
                        adjustmentMode === AdjustmentMode.ADJUST_WP_TO_WP
                        && (
                            codeUpper === PL_FC_LI_CODE
                            || codeUpper === PL_AE_MPF_CODE
                            || codeUpper === PL_AE_REN_CODE
                            || (
                                isPpeOrDepFsCode(codeUpper)
                                && !ppeDepnNbvCross.active
                                // 其它 WP → PPE 根节点：计入父节点搜索，不展开分类
                                && !isInboundPpeRootOnlySelection(fromPaperCode, codeUpper)
                            )
                        )
                    ) {
                        // 二级菜单搜索项不计入父节点重名
                    } else {
                        const displayName = resolveFsSelectionDisplayName(n, adjustmentMode);
                        if (displayName) {
                            const key = `${displayName}::${tab}`;
                            nameTabCount.set(key, (nameTabCount.get(key) || 0) + 1);
                        }
                    }
                }
                if (n.children && n.children.length > 0) {
                    countNames(n.children, tab);
                }
            });
        };

        countNames(bsTree, 'bs');
        countNames(plTree, 'pl');

        // 第二次遍历：收集选项，为重复名称添加标识和序号
        const collectOptions = (nodes: AuditFsTreeSelection[], tab: 'bs' | 'pl') => {
            nodes.forEach(n => {
                if (isSearchableFsNode(n)) {
                    const codeUpper = String(n.code || '').trim().toUpperCase();
                    // WP_TO_WP：PL-FC-LI / PL-AE-MPF 本身不可直接选，改为二级选项；PL-AE-REN 直接选节点
                    if (
                        adjustmentMode === AdjustmentMode.ADJUST_WP_TO_WP
                        && codeUpper === PL_FC_LI_CODE
                    ) {
                        PL_FC_LI_INTEREST_SUBMENU.forEach((opt) => {
                            options.push({
                                label: opt.label,
                                displayLabel: opt.label,
                                code: PL_FC_LI_CODE,
                                value: `${n.fsId}::${opt.wpFetchCode}`,
                                fsId: n.fsId,
                                tab,
                                node: n,
                                wpFetchCode: opt.wpFetchCode,
                            });
                        });
                    } else if (
                        adjustmentMode === AdjustmentMode.ADJUST_WP_TO_WP
                        && codeUpper === PL_AE_MPF_CODE
                    ) {
                        PL_AE_MPF_SUBMENU.forEach((opt) => {
                            options.push({
                                label: opt.label,
                                displayLabel: opt.label,
                                code: PL_AE_MPF_CODE,
                                value: `${n.fsId}::${opt.wpFetchCode}`,
                                fsId: n.fsId,
                                tab,
                                node: n,
                                wpFetchCode: opt.wpFetchCode,
                            });
                        });
                    } else if (
                        adjustmentMode === AdjustmentMode.ADJUST_WP_TO_WP
                        && isPpeOrDepFsCode(codeUpper)
                        && !ppeDepnNbvCross.active
                        // 其它 WP → PropertyPlantEquipment：搜索选根节点，不列 xxxRow 分类
                        && !isInboundPpeRootOnlySelection(fromPaperCode, codeUpper)
                    ) {
                        ppeCategoryOptions.forEach((opt) => {
                            options.push({
                                label: opt.label,
                                displayLabel: opt.label,
                                code: codeUpper,
                                value: `${n.fsId}::${opt.key}`,
                                fsId: n.fsId,
                                tab,
                                node: n,
                                wpFetchCode: opt.key,
                            });
                        });
                    } else {
                        const displayName = resolveFsSelectionDisplayName(n, adjustmentMode);
                        if (displayName) {
                            const key = `${displayName}::${tab}`;
                            const count = nameTabCount.get(key) || 0;
                            const tabLabel = tab === 'bs' ? 'BS' : 'PL';

                            const currentIndex = (nameTabIndex.get(key) || 0) + 1;
                            nameTabIndex.set(key, currentIndex);

                            let displayLabel = displayName;
                            if (count > 1) {
                                displayLabel = `${displayName} (${tabLabel} #${currentIndex})`;
                            }

                            options.push({
                                label: displayName,
                                displayLabel,
                                code: n.code || '',
                                value: `${n.fsId}::${displayName}`,
                                fsId: n.fsId,
                                tab,
                                node: n,
                            });
                        }
                    }
                }
                if (n.children && n.children.length > 0) {
                    collectOptions(n.children, tab);
                }
            });
        };

        collectOptions(bsTree, 'bs');
        collectOptions(plTree, 'pl');

        return options;
    }, [bsTree, plTree, adjustmentMode, ppeCategoryOptions, ppeDepnNbvCross, fromPaperCode]);

    // 查找节点路径（用于展开父节点）
    const findNodePath = (nodes: AuditFsTreeSelection[], targetFsId: number, path: number[] = []): number[] | null => {
        for (const node of nodes) {
            const currentPath = [...path, node.fsId];
            if (node.fsId === targetFsId) {
                return currentPath;
            }
            if (node.children && node.children.length > 0) {
                const found = findNodePath(node.children, targetFsId, currentPath);
                if (found) return found;
            }
        }
        return null;
    };

    // 处理搜索选择
    const handleSearchSelect = useCallback((_event: any, selected: typeof searchOptions[number] | string | null) => {
        if (!selected || typeof selected === 'string') {
            setExpandedNodes(new Set());
            return;
        }

        // 切换到对应的 tab
        setActiveTab(selected.tab);

        // 查找并展开路径
        const tree = selected.tab === 'bs' ? bsTree : plTree;
        const path = findNodePath(tree, selected.fsId);

        if (path) {
            setExpandedNodes(new Set(path));

            setTimeout(() => {
                const element = document.querySelector(`[data-fsid="${selected.fsId}"]`);
                if (element) {
                    element.scrollIntoView({behavior: 'smooth', block: 'center'});
                    // 二级菜单项：直接确认（带 wpFetchCode）；普通节点有 code 则选中
                    if (selected.wpFetchCode && selected.node?.code && onConfirm) {
                        handleChipClick(selected.node.code, selected.node, selected.wpFetchCode);
                    } else if (
                        selected.node?.code
                        && String(selected.node.code).trim().toUpperCase() !== PL_FC_LI_CODE
                        && String(selected.node.code).trim().toUpperCase() !== PL_AE_MPF_CODE
                        && (
                            !isPpeOrDepFsCode(String(selected.node.code).trim().toUpperCase())
                            // 其它 WP → PPE 根节点：无 wpFetchCode，仍直接确认
                            || isInboundPpeRootOnlySelection(fromPaperCode, selected.node.code)
                        )
                        && onConfirm
                    ) {
                        handleChipClick(selected.node.code, selected.node);
                    }
                }
            }, 100);
        }

        setSearchInput('');
    }, [bsTree, plTree, onConfirm, handleChipClick, fromPaperCode]);

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{sx: {width: {xs: '100%', sm: 480}, display: 'flex', flexDirection: 'column'}}}
                sx={{zIndex: (theme) => theme.zIndex.modal + 10}}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: theme => `1px solid ${theme.palette.divider}`
                }}>
                    <Typography variant="h6" sx={{fontWeight: 600}}>Adjust To</Typography>
                    <Button onClick={onClose}>Close</Button>
                </Box>

                {/* MULTI: YOA selector only for BS non-multi initiator before anchor (gap doc §4.1) */}
                {showDrawerYoaSelector && periodLockCtx && (
                    <Box sx={{px: 2, pt: 1, borderBottom: 1, borderColor: 'divider'}}>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.5}}>
                            Target period (required for PL / multi-year BS)
                        </Typography>
                        <Tabs
                            value={drawerSliceKey || false}
                            onChange={(_, v) => setDrawerSliceKey(String(v))}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{minHeight: 36, '& .MuiTab-root': {minHeight: 36, py: 0}}}
                        >
                            {periodLockCtx.periods.map((s) => (
                                <Tab
                                    key={s.key}
                                    value={s.key}
                                    label={s.yearOfAssessment || s.label}
                                />
                            ))}
                        </Tabs>
                    </Box>
                )}

                {/* Search Section */}
                {!loading && (
                    <Box sx={{px: 2, pt: 2, pb: 1}}>
                        <Autocomplete
                            options={searchOptions}
                            getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.displayLabel}
                            filterOptions={(options, {inputValue}) => {
                                const lower = inputValue.toLowerCase().trim();
                                if (!lower) return options;
                                return options.filter(opt =>
                                    opt.displayLabel.toLowerCase().includes(lower) ||
                                    (opt.code && opt.code.toLowerCase().includes(lower))
                                );
                            }}
                            renderOption={(props, opt) => (
                                <li {...props} key={opt.value}>
                                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                        <Typography variant="body2">{opt.displayLabel}</Typography>
                                        {opt.code && (
                                            <Typography variant="caption" color="text.secondary">
                                                {opt.code}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            inputValue={searchInput}
                            onInputChange={(_, v) => setSearchInput(v)}
                            onChange={handleSearchSelect}
                            freeSolo
                            size="small"
                            fullWidth
                            slotProps={{
                                popper: {style: {zIndex: 9999}}
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by code or name..."
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        />
                    </Box>
                )}

                {/* Fixed Tabs Section */}
                {!loading && (
                    <Box sx={{px: 2, borderBottom: 1, borderColor: 'divider'}}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, v) => setActiveTab(v)}
                        >
                            <Tab value="bs" label="Balance Sheet"/>
                            <Tab value="pl" label="Income Statement"/>
                        </Tabs>
                    </Box>
                )}

                {/* Scrollable Content Area */}
                <Box sx={{flex: 1, overflow: 'auto', px: 2, pb: 2, position: 'relative'}}>
                    {(ppeCheckLoading || ppeCategoryCacheLoading) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(255,255,255,0.55)',
                            }}
                        >
                            <CircularProgress size={28}/>
                        </Box>
                    )}
                    {loading ? (
                        <Box sx={{py: 2}}>
                            {/* Loading skeleton */}
                            {[1, 2, 3, 4, 5].map(i => (
                                <Box key={i} sx={{mb: 2}}>
                                    <Skeleton variant="text" width="60%" height={30}/>
                                    <Box sx={{pl: 3}}>
                                        <Skeleton variant="text" width="40%" height={25}/>
                                        <Skeleton variant="text" width="45%" height={25}/>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <>
                            {/* Balance Sheet Content */}
                            {activeTab === 'bs' && (
                                <>
                                    {bsTree.length > 0 ? (
                                        <List>
                                            {bsTree.map((n, idx) => (
                                                <NodeRow
                                                    key={`bs-${n.fsId}-${idx}`}
                                                    node={n}
                                                    depth={0}
                                                    onLaunch={handleLaunch}
                                                    onChipClick={handleChipClick}
                                                    forceOpen={expandedNodes.has(n.fsId)}
                                                    adjustmentMode={adjustmentMode}
                                                    ppeCategoryOptions={ppeCategoryOptions}
                                                />
                                            ))}
                                        </List>
                                    ) : (
                                        <Box sx={{py: 2}}>
                                            <Typography variant="body2" color="text.secondary">No Balance Sheet
                                                Data</Typography>
                                        </Box>
                                    )}
                                </>
                            )}

                            {/* Income Statement Content */}
                            {activeTab === 'pl' && (
                                <>
                                    {plTree.length > 0 ? (
                                        <List>
                                            {plTree.map((n, idx) => (
                                                <NodeRow
                                                    key={`pl-${n.fsId}-${idx}`}
                                                    node={n}
                                                    depth={0}
                                                    onLaunch={handleLaunch}
                                                    onChipClick={handleChipClick}
                                                    forceOpen={expandedNodes.has(n.fsId)}
                                                    adjustmentMode={adjustmentMode}
                                                    ppeCategoryOptions={ppeCategoryOptions}
                                                />
                                            ))}
                                        </List>
                                    ) : (
                                        <Box sx={{py: 2}}>
                                            <Typography variant="body2" color="text.secondary">No Income Statement
                                                Data</Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Drawer>

            <Snackbar
                open={!!periodLockSnack}
                autoHideDuration={4000}
                onClose={() => setPeriodLockSnack(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert severity="warning" onClose={() => setPeriodLockSnack(null)} variant="filled">
                    {periodLockSnack}
                </Alert>
            </Snackbar>

            {/* Dynamic launcher (used when onConfirm is not provided) */}
            {!onConfirm && (
                <AdjustDialogLauncher
                    code={launchCode ?? undefined}
                    open={launchOpen}
                    onClose={handleChildClose}
                    payload={launchPayload}
                />
            )}

            {/* 仅 Vehicle Loan PPE 空数据提醒；zIndex 高于本 Drawer，不影响共享 EmptyConfirmDialog */}
            <Dialog
                open={ppeEmptyConfirmOpen}
                onClose={handlePpeEmptyCancel}
                sx={{zIndex: (theme) => theme.zIndex.modal + 20}}
            >
                <DialogTitle>Confirmation</DialogTitle>
                <DialogContent dividers>
                    <Typography>{PPE_EMPTY_CONFIRM_MESSAGE}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handlePpeEmptyConfirm}>Yes</Button>
                    <Button variant="outlined" onClick={handlePpeEmptyCancel}>No</Button>
                </DialogActions>
            </Dialog>

            {(ppeCheckLoading || ppeCategoryCacheLoading) && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: (theme) => theme.zIndex.modal + 15,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.35)',
                        pointerEvents: 'none',
                    }}
                >
                    <CircularProgress size={32}/>
                </Box>
            )}

            {error && (
                <Snackbar
                    open={!!error}
                    autoHideDuration={3000}
                >
                    <Alert severity="error">{error}</Alert>
                </Snackbar>
            )}
        </>
    );
}

// ✅ 使用 React.memo 包装组件，避免不必要的重新渲染
export default React.memo(FsSelectionDrawerComponent, (prevProps, nextProps) => {
    // 自定义比较逻辑
    return (
        prevProps.open === nextProps.open &&
        prevProps.onClose === nextProps.onClose &&
        prevProps.onConfirm === nextProps.onConfirm &&
        prevProps.payload?.companyId === nextProps.payload?.companyId &&
        prevProps.payload?.annualId === nextProps.payload?.annualId &&
        prevProps.payload?.fsId === nextProps.payload?.fsId &&
        prevProps.excludeFsIds?.join(',') === nextProps.excludeFsIds?.join(',') &&
        prevProps.excludeFilter === nextProps.excludeFilter &&
        prevProps.adjustmentMode === nextProps.adjustmentMode
    );
});
